import { makeAutoObservable, runInAction } from "mobx";
import { CalendarService } from "src/services/calendar.service";
import { toast } from "src/components/ui/toast";
import type {
  CalendarConnection,
  BookingEventType,
  Booking,
} from "src/types/calendar.types";

export default class CalendarStore {
  connections: CalendarConnection[] = [];
  loadingConnections = false;
  connectionsError: string | null = null;

  eventTypes: BookingEventType[] = [];
  loadingEventTypes = false;

  bookings: Booking[] = [];
  loadingBookings = false;

  constructor() {
    makeAutoObservable(this);
  }

  async loadConnections() {
    this.loadingConnections = true;
    this.connectionsError = null;
    try {
      const { data } = await CalendarService.listConnections();
      runInAction(() => {
        this.connections = data;
      });
    } catch (e: any) {
      runInAction(() => {
        this.connectionsError = e?.message || "Failed to load connections";
      });
    } finally {
      runInAction(() => {
        this.loadingConnections = false;
      });
    }
  }

  startGoogleOAuth(reconnectId?: string) {
    // Open the popup SYNCHRONOUSLY in response to the user gesture.
    // Browsers block window.open() called from an async .then() callback
    // because the click gesture is considered consumed by then.
    const popup = window.open("about:blank", "googleOAuth", "width=520,height=640");
    if (!popup) {
      toast.error("Popup blocked — allow popups for this site and try again");
      return;
    }
    const onMessage = (e: MessageEvent) => {
      const d = e.data || {};
      if (d.source !== "botbat:googleOAuth") return;
      window.removeEventListener("message", onMessage);
      try {
        popup.close();
      } catch {}
      if (d.ok) {
        toast.success("Calendar connected");
        this.loadConnections();
      } else {
        toast.error(d.error || "Failed to connect calendar");
      }
    };
    window.addEventListener("message", onMessage);

    CalendarService.getAuthUrl({ reconnectId })
      .then(({ url }) => {
        popup.location.href = url;
      })
      .catch((err: any) => {
        try {
          popup.close();
        } catch {}
        window.removeEventListener("message", onMessage);
        toast.error(err?.response?.data?.message || err?.message || "Failed to start OAuth");
      });
  }

  async disconnect(id: string) {
    await CalendarService.disconnect(id);
    runInAction(() => {
      this.connections = this.connections.filter((c) => c.id !== id);
    });
    toast.success("Calendar disconnected");
  }

  async refreshCalendars(id: string) {
    const { data } = await CalendarService.refreshCalendars(id);
    runInAction(() => {
      const i = this.connections.findIndex((c) => c.id === id);
      if (i >= 0) this.connections[i] = data;
    });
  }

  async loadEventTypes() {
    this.loadingEventTypes = true;
    try {
      const data = await CalendarService.listEventTypes();
      runInAction(() => {
        this.eventTypes = data;
      });
    } finally {
      runInAction(() => {
        this.loadingEventTypes = false;
      });
    }
  }

  async loadBookings(
    filters?: Parameters<typeof CalendarService.listBookings>[0],
  ) {
    this.loadingBookings = true;
    try {
      const { data } = await CalendarService.listBookings(filters);
      runInAction(() => {
        this.bookings = data;
      });
    } finally {
      runInAction(() => {
        this.loadingBookings = false;
      });
    }
  }

  async cancelBooking(id: string) {
    await CalendarService.cancelBooking(id);
    runInAction(() => {
      const i = this.bookings.findIndex((b) => b.id === id);
      if (i >= 0)
        this.bookings[i] = { ...this.bookings[i], status: "cancelled" };
    });
    toast.success("Booking cancelled");
  }
}
